import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Lock, Mail, Eye, EyeOff, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ClientLoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/clients/login`, { email, password }, { withCredentials: true });
      await checkAuth();
      toast.success("Login successful!");
      navigate("/client-portal");
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid credentials";
      toast.error(typeof message === "string" ? message : "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10] flex items-center justify-center px-4" data-testid="client-login-page">
      <div className="w-full max-w-md">
        <div className="bg-[#15161C] border border-[#2A2B32] rounded-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-[#4A2B6E] via-[#0D5C63] to-[#FF6B35] p-0.5 mx-auto mb-4">
              <div className="w-full h-full rounded-full bg-[#0B0C10] flex items-center justify-center">
                <Users size={28} className="text-[#FF6B35]" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white">Client Portal</h1>
            <p className="text-[#A0AAB2] text-sm mt-2">New Leaf PMC LLC</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-white mb-2">
                <Mail size={16} className="text-[#FF6B35]" />
                <span>Email</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                placeholder="your@email.com"
                data-testid="client-login-email"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-white mb-2">
                <Lock size={16} className="text-[#FF6B35]" />
                <span>Password</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                  placeholder="Enter password"
                  data-testid="client-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A0AAB2] hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full gradient-btn py-4 rounded-lg font-bold text-lg disabled:opacity-50"
              data-testid="client-login-submit"
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#A0AAB2] text-sm">
              Need an account? Contact us at{" "}
              <a href="tel:863-340-9244" className="text-[#FF6B35] hover:underline">
                863-340-9244
              </a>
            </p>
          </div>

          <div className="mt-4 pt-4 border-t border-[#2A2B32] text-center">
            <Link to="/login" className="text-[#A0AAB2] text-sm hover:text-white">
              Admin Login →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientLoginPage;
